import React from 'react';
import { Youtube, Check, ExternalLink, Loader2, Play } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { useYouTubePublish } from '@/hooks/useYouTubePublish';

// YouTube publish modal — ported from the legacy enhancer; drives the
// useYouTubePublish hook (contract: POST /api/youtube/upload).

interface YouTubePublishDialogProps {
  youtube: ReturnType<typeof useYouTubePublish>;
}

export const YouTubePublishDialog: React.FC<YouTubePublishDialogProps> = ({ youtube }) => {
  const {
    showYoutubePublishModal, setShowYoutubePublishModal,
    youtubePublishData, setYoutubePublishData,
    youtubePublishForm, setYoutubePublishForm,
    youtubeUploading, youtubeUploadResult, setYoutubeUploadResult,
    uploadToYoutube,
  } = youtube as any;

  const close = () => {
    setShowYoutubePublishModal(false);
    setYoutubePublishData?.(null);
    setYoutubeUploadResult?.(null);
  };

  return (
    <Dialog open={showYoutubePublishModal} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-600" />
            Publish to YouTube
          </DialogTitle>
        </DialogHeader>

        {youtubeUploadResult ? (
          <div className="space-y-4 text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Upload Complete!</h3>
              <p className="text-muted-foreground text-sm mt-1">Your video has been uploaded to YouTube.</p>
            </div>
            <Button className="w-full" onClick={() => window.open(youtubeUploadResult.url, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-2" />
              View on YouTube
            </Button>
            <Button variant="outline" className="w-full" onClick={close}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {youtubePublishData && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <div className="w-24 h-14 bg-black rounded overflow-hidden flex-shrink-0">
                  {youtubePublishData.thumbnailUrl ? (
                    <img src={youtubePublishData.thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-white/70" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{youtubePublishData.name}</p>
                  <p className="text-xs text-muted-foreground">Ready to upload</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="youtube-title">Title *</Label>
              <Input
                id="youtube-title"
                value={youtubePublishForm.title}
                onChange={(e) => setYoutubePublishForm((prev: any) => ({ ...prev, title: e.target.value }))}
                placeholder="Video title..."
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube-description">Description</Label>
              <Textarea
                id="youtube-description"
                value={youtubePublishForm.description}
                onChange={(e) => setYoutubePublishForm((prev: any) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your video..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube-tags">Tags (comma-separated)</Label>
              <Input
                id="youtube-tags"
                value={youtubePublishForm.tags}
                onChange={(e) => setYoutubePublishForm((prev: any) => ({ ...prev, tags: e.target.value }))}
                placeholder="casting, video, demo..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube-privacy">Privacy</Label>
              <Select
                value={youtubePublishForm.privacy}
                onValueChange={(value) => setYoutubePublishForm((prev: any) => ({ ...prev, privacy: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private - Only you can view</SelectItem>
                  <SelectItem value="unlisted">Unlisted - Anyone with link can view</SelectItem>
                  <SelectItem value="public">Public - Everyone can view</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={close} disabled={youtubeUploading}>
                Cancel
              </Button>
              <Button
                onClick={uploadToYoutube}
                disabled={youtubeUploading || !youtubePublishForm.title.trim()}
                className="bg-red-600 hover:bg-red-700"
              >
                {youtubeUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Youtube className="w-4 h-4 mr-2" />Upload to YouTube</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
